/**
 * Raketech Recon Diff
 *
 * Compares Raketech's original reconciliation (Mar 2023 – Nov 2024, held in D1)
 * against the replacement workbook they sent covering Mar 2023 – May 2026.
 *
 * This page is additive: it reads from /api/raketech/recon-diff and
 * /api/raketech/recon-new, both new endpoints, plus the existing
 * /api/raketech/compare for our own bottom line. No existing report is affected.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Database,
  Download,
  FileText,
  Filter,
  GitCompareArrows,
  Loader2,
  RefreshCw,
  Scale,
  Search,
  Sigma,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

import type {
  AccountDiffRow,
  BridgeStep,
  NewReconDataset,
  ReconDiffData,
} from '../types/raketechReconDiff.types';
import { buildDiff, buildTheirBridge, feesFromLedger } from '../lib/raketechReconDiff';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) return JSON.parse(auth).state?.token || null;
  } catch { /* ignore */ }
  return null;
};

const fmtEUR = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

const fmtEURExact = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtPct = (v: number | null) => (v === null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);

const fmtMonth = (month: string) => {
  const [year, m] = month.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[Number(m) - 1]} ${year.slice(2)}`;
};

type MainTab = 'deltas' | 'bridge' | 'coverage' | 'tail' | 'audit';
type DeltaFilter = 'all' | 'increased' | 'decreased' | 'new_only' | 'old_only' | 'unchanged' | 'reorganised';

/** Our own bottom line, from the endpoint that already backs the report pages. */
interface CompareTotals {
  invoiced: number;
  actualInvoiced: number;
  actualInvoiceCount: number;
  shouldBe: number;
  reconEarnings: number;
  partnerOurShare: number;
  adwstatsSynced: number;
  manualCheck: number;
  positiveCorrections: number;
  negativeCorrections: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RaketechReconDiff: React.FC = () => {
  const [dataset, setDataset] = useState<NewReconDataset | null>(null);
  const [oldData, setOldData] = useState<ReconDiffData | null>(null);
  const [ourTotals, setOurTotals] = useState<CompareTotals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOurs, setIsLoadingOurs] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<MainTab>('deltas');
  const [deltaFilter, setDeltaFilter] = useState<DeltaFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [applyMargin, setApplyMargin] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // -----------------------------------------------------------------------
  // Load both sides
  // -----------------------------------------------------------------------
  const load = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { setError('Not authenticated'); setIsLoading(false); return; }

    setIsLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [newResp, oldResp] = await Promise.all([
        fetch('/api/raketech/recon-new', { headers }),
        fetch('/api/raketech/recon-diff', { headers }),
      ]);
      const newJson = await newResp.json() as any;
      const oldJson = await oldResp.json() as any;
      if (!newJson.success) throw new Error(newJson.error || 'Could not load the new workbook');
      if (!oldJson.success) throw new Error(oldJson.error || 'Could not load the original reconciliation');
      setDataset(newJson.data as NewReconDataset);
      setOldData(oldJson.data as ReconDiffData);
    } catch (err: any) {
      setError(err.message || 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Our bottom line is only needed for the bridge, and the compare endpoint is
   *  expensive, so it is fetched the first time that tab is opened. */
  const loadOurs = useCallback(async () => {
    const token = getAuthToken();
    if (!token || ourTotals || isLoadingOurs) return;
    setIsLoadingOurs(true);
    try {
      const resp = await fetch('/api/raketech/compare?invoicePeriod=all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json() as any;
      if (json.success && json.data?.totals) setOurTotals(json.data.totals as CompareTotals);
      else if (json.data?.empty) toast.info('No Raketech data imported yet, so our own bottom line is unavailable.');
    } catch {
      toast.error('Could not load our own bottom line from /api/raketech/compare');
    } finally {
      setIsLoadingOurs(false);
    }
  }, [ourTotals, isLoadingOurs]);

  useEffect(() => {
    if (activeTab === 'bridge') loadOurs();
  }, [activeTab, loadOurs]);

  // -----------------------------------------------------------------------
  // Derived comparison
  // -----------------------------------------------------------------------
  const diff = useMemo(
    () => (dataset && oldData ? buildDiff(dataset, oldData, { applyMargin }) : null),
    [dataset, oldData, applyMargin],
  );

  const theirBridge = useMemo(() => (dataset ? buildTheirBridge(dataset) : []), [dataset]);
  const ledgerFees = useMemo(() => (dataset ? feesFromLedger(dataset) : null), [dataset]);

  const filteredRows = useMemo(() => {
    if (!diff) return [];
    let rows = diff.rows;

    switch (deltaFilter) {
      case 'increased': rows = rows.filter((r) => r.delta > 1); break;
      case 'decreased': rows = rows.filter((r) => r.delta < -1); break;
      case 'new_only': rows = rows.filter((r) => r.matchMethod === 'new_only'); break;
      case 'old_only': rows = rows.filter((r) => r.matchMethod === 'old_only'); break;
      case 'unchanged': rows = rows.filter((r) => Math.abs(r.delta) <= 1); break;
      case 'reorganised': rows = rows.filter((r) => r.reorganised); break;
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((r) =>
        r.partner.toLowerCase().includes(q) ||
        r.brands.some((b) => b.toLowerCase().includes(q)) ||
        r.offers.some((o) => o.offer.toLowerCase().includes(q)) ||
        r.hubId.toLowerCase().includes(q) ||
        r.acIds.some((id) => id.includes(q)) ||
        r.geos.some((g) => g.toLowerCase().includes(q)));
    }
    return rows;
  }, [diff, deltaFilter, searchTerm]);

  const toggleRow = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const exportCsv = useCallback(() => {
    if (!filteredRows.length) return;
    // One line per offer so the export keeps the finest granularity available,
    // with its account totals repeated for pivoting.
    const header = [
      'Account match', 'HUB ID', 'AC IDs', 'Partner', 'Brand', 'Offer', 'Geo', 'Partner account',
      'Offer in old', 'Offer in new', 'Offer old (EUR)', 'Offer new (EUR)', 'Offer delta (EUR)',
      'Account old (EUR)', 'Account new (EUR)', 'Account new gross (EUR)', 'Account delta (EUR)', 'Account delta %',
      'Account tail (EUR)', 'Our synced tail (EUR)', 'Reorganised', 'In adwstats',
    ];
    const lines = filteredRows.flatMap((r) => {
      const accountCells = [
        r.oldOverlap, r.newOverlap, r.newOverlapGross, r.delta, r.deltaPct ?? '',
        r.newTail, r.syncedTail, r.reorganised ? 'yes' : 'no', r.existsInAdwstats ? 'yes' : 'no',
      ];
      if (r.offers.length === 0) {
        return [[r.matchMethod, r.hubId, r.acIds.join(' '), r.partner, r.brands.join(' / '), '', r.geos.join(' '),
          r.pa ? 'yes' : 'no', '', '', '', '', '', ...accountCells]];
      }
      return r.offers.map((o) => [
        r.matchMethod, r.hubId, r.acIds.join(' '), r.partner, o.brand, o.offer, r.geos.join(' '),
        o.pa ? 'yes' : 'no', o.inOld ? 'yes' : 'no', o.inNew ? 'yes' : 'no', o.old, o.new, o.delta,
        ...accountCells,
      ]);
    });
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `raketech-recon-diff-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredRows]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading both reconciliations…</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dataset || !oldData || !diff) {
    return (
      <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Comparison unavailable</h2>
          <p className="text-sm text-zinc-400 mb-6">{error || 'Missing data'}</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-500 hover:bg-purple-600 text-white font-medium text-sm rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { summary, coverage } = diff;
  const controls = dataset.controls;

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 animate-fade-in">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <GitCompareArrows className="h-7 w-7 text-cyan-400" />
              Recon: Old vs New
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Their original reconciliation (Mar 2023 – Nov 2024) against{' '}
              <span className="text-zinc-300">{dataset.source}</span> (Mar 2023 – May 2026).
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={applyMargin}
                onChange={(e) => setApplyMargin(e.target.checked)}
                className="accent-cyan-500"
              />
              Normalise 5% partner margin
            </label>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
            <button
              onClick={load}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard
          label="Their bottom line"
          value={fmtEUR(controls.underpaymentAfterFees ?? 0)}
          hint="Underpayment they admit"
          icon={<Scale className="h-4 w-4" />}
          color="amber"
        />
        <StatCard
          label="Old recon, overlap"
          value={fmtEUR(summary.oldOverlapTotal)}
          hint={`${summary.overlapMonths.length} months, Mar 23 – Nov 24`}
          icon={<FileText className="h-4 w-4" />}
          color="zinc"
        />
        <StatCard
          label="New recon, same months"
          value={fmtEUR(summary.newOverlapTotal)}
          hint={applyMargin ? 'Margin normalised' : 'Gross, as stated'}
          icon={<FileText className="h-4 w-4" />}
          color="cyan"
        />
        <StatCard
          label="Restatement on overlap"
          value={`${summary.overlapDelta > 0 ? '+' : ''}${fmtEUR(summary.overlapDelta)}`}
          hint={fmtPct(summary.overlapDeltaPct)}
          icon={summary.overlapDelta >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          color={summary.overlapDelta >= 0 ? 'emerald' : 'red'}
        />
        <StatCard
          label="New period, Dec 24 →"
          value={fmtEUR(summary.newTailTotal)}
          hint="No old baseline exists"
          icon={<Sigma className="h-4 w-4" />}
          color="purple"
        />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4 border-b border-[hsl(225,15%,15%)] pb-3">
        {([
          ['deltas', `Restatements (${diff.rows.length})`],
          ['bridge', 'Bottom line'],
          ['coverage', `Scope gaps (${summary.newOnlyAccounts + summary.oldOnlyAccounts})`],
          ['tail', 'Dec 24 – May 26'],
          ['audit', 'Their own math'],
        ] as [MainTab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors ${
              activeTab === key
                ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Restatements */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'deltas' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            One row per account, comparing only the {summary.overlapMonths.length} months both
            reconciliations cover. Accounts are the unit because Raketech reorganised offers between the
            two reports — {summary.reorganisedAccounts} accounts have offers on one side that do not
            appear on the other, so an offer-to-offer comparison would report consolidated offers as
            deletions. Expand a row for its offer and month breakdown. Their original figures are net of
            Raketech's 5% share while the new workbook's monthly columns are gross, so with
            normalisation on, partner accounts are multiplied by {(1 - dataset.marginRate).toFixed(2)} up
            to {dataset.period1End} to put both sides on the same basis. Raketech stopped taking the share
            after that, so later months are already gross on both sides and are left alone.
          </Explainer>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              <Filter className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              {([
                ['all', `All (${diff.rows.length})`],
                ['increased', `Revised up (${diff.rows.filter((r) => r.delta > 1).length})`],
                ['decreased', `Revised down (${diff.rows.filter((r) => r.delta < -1).length})`],
                ['unchanged', `Unchanged (${diff.rows.filter((r) => Math.abs(r.delta) <= 1).length})`],
                ['reorganised', `Offers reorganised (${summary.reorganisedAccounts})`],
                ['new_only', `New only (${summary.newOnlyAccounts})`],
                ['old_only', `Dropped (${summary.oldOnlyAccounts})`],
              ] as [DeltaFilter, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setDeltaFilter(key)}
                  className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                    deltaFilter === key
                      ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                      : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Partner, brand, offer, ID…"
                className="pl-8 pr-3 py-1.5 w-64 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
              />
            </div>
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="w-6" />
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Partner / Brands</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Match</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Old recon</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">New recon</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Change</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">%</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Dec 24 →</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {filteredRows.slice(0, 400).map((row) => (
                    <React.Fragment key={row.key}>
                      <tr
                        onClick={() => toggleRow(row.key)}
                        className="hover:bg-[hsl(225,15%,12%)] cursor-pointer transition-colors"
                      >
                        <td className="pl-2">
                          {expanded.has(row.key)
                            ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
                            : <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-zinc-200 truncate max-w-[200px]">{row.partner || '—'}</div>
                          <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">
                            {row.brands.slice(0, 3).join(', ') || '—'}
                            {row.brands.length > 3 && ` +${row.brands.length - 3}`}
                            {row.geos.length > 0 && <span className="text-cyan-500/70"> · {row.geos.join(', ')}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-zinc-400 text-[10px]">
                            HUB {row.hubId || '—'}
                            {row.acIds.length > 0 && <span className="text-zinc-600"> · AC {row.acIds.join(', ')}</span>}
                          </div>
                          <div className="text-[10px] text-zinc-600">
                            {row.offers.length} offer{row.offers.length === 1 ? '' : 's'}
                            {row.pa && <span className="text-amber-500/70"> · PA</span>}
                            {row.reorganised && <span className="text-blue-400/70"> · reorganised</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2"><MatchBadge method={row.matchMethod} /></td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                          {row.oldOverlap ? fmtEUR(row.oldOverlap) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-200">
                          {row.newOverlap ? fmtEUR(row.newOverlap) : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${
                          row.delta > 1 ? 'text-emerald-400' : row.delta < -1 ? 'text-red-400' : 'text-zinc-600'
                        }`}>
                          {Math.abs(row.delta) <= 1 ? '—' : `${row.delta > 0 ? '+' : ''}${fmtEUR(row.delta)}`}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums text-[10px] ${
                          (row.deltaPct ?? 0) > 0 ? 'text-emerald-500/80' : (row.deltaPct ?? 0) < 0 ? 'text-red-500/80' : 'text-zinc-600'
                        }`}>
                          {Math.abs(row.delta) <= 1 ? '' : fmtPct(row.deltaPct)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-purple-400/80">
                          {row.newTail ? fmtEUR(row.newTail) : '—'}
                        </td>
                      </tr>

                      {expanded.has(row.key) && (
                        <tr className="bg-[hsl(225,15%,8%)]">
                          <td />
                          <td colSpan={8} className="px-3 py-3">
                            <AccountDetail row={row} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredRows.length > 400 && (
              <div className="px-4 py-2 text-[11px] text-zinc-500 border-t border-[hsl(225,15%,15%)]">
                Showing the 400 largest changes of {filteredRows.length}. Export the CSV for the full list.
              </div>
            )}
            {filteredRows.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">Nothing matches these filters</div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Bottom line */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'bridge' && (
        <div className="space-y-5 animate-fade-in">
          <Explainer>
            Raketech's workbook concludes they owe {fmtEURExact(controls.underpaymentAfterFees ?? 0)}.
            Our own reconciliation reaches a much larger number. Both chains are laid out below with
            their sources, followed by the items that account for the distance between them.
          </Explainer>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Their chain */}
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)]">
                <h3 className="text-sm font-semibold text-white">Raketech's chain</h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">As stated in {dataset.source}</p>
              </div>
              <div className="divide-y divide-[hsl(225,15%,14%)]">
                {theirBridge.map((step, index) => <BridgeRow key={index} step={step} />)}
              </div>
            </div>

            {/* Our chain */}
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)] flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-white">Our chain</h3>
                  <p className="text-[11px] text-zinc-500 mt-0.5">From the existing Raketech compare engine</p>
                </div>
                {isLoadingOurs && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
              </div>
              {ourTotals ? (
                <div className="divide-y divide-[hsl(225,15%,14%)]">
                  <BridgeRow step={{
                    label: 'Best evidence of what we earned',
                    amount: ourTotals.shouldBe,
                    kind: 'figure',
                    note: 'Per account, the highest of their recon, our synced data less 5%, and our manual backend checks less 5%.',
                  }} />
                  <BridgeRow step={{
                    label: 'Less: what they invoiced us on commission sheets',
                    amount: -ourTotals.invoiced,
                    kind: 'deduction',
                  }} />
                  <BridgeRow step={{
                    label: 'Underpayment, per our reconciliation',
                    amount: ourTotals.shouldBe - ourTotals.invoiced,
                    kind: 'result',
                    note: 'The Final Report adds post-recon AC data on top of this.',
                  }} />
                  <BridgeRow step={{
                    label: 'For reference: their actual invoices to us',
                    amount: ourTotals.actualInvoiced,
                    kind: 'figure',
                    note: `${ourTotals.actualInvoiceCount} invoices on record. Their workbook states ${fmtEURExact(controls.totalPaid ?? 0)} paid.`,
                    discrepancy: ourTotals.actualInvoiced > 0
                      ? Math.round(((controls.totalPaid ?? 0) - ourTotals.actualInvoiced) * 100) / 100
                      : undefined,
                  }} />
                </div>
              ) : (
                <div className="p-6 text-center text-xs text-zinc-500">
                  {isLoadingOurs ? 'Computing our bottom line…' : 'Our bottom line is unavailable.'}
                </div>
              )}
            </div>
          </div>

          {/* Reconciling items */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)]">
              <h3 className="text-sm font-semibold text-white">What separates the two</h3>
            </div>
            <div className="divide-y divide-[hsl(225,15%,14%)]">
              <ReconcilingItem
                label="Scope"
                amount={controls.statedTotalEarnings - summary.oldOverlapTotal}
                detail={`Their workbook covers ${dataset.offers.length} offers across ${dataset.summary.partners.length} partners and ${dataset.months.length} months. Our reconciliation covers ${oldData.oldOfferTotals.offers} offers across ${oldData.oldOfferTotals.accounts} accounts and ${summary.overlapMonths.length} months. ${summary.newOnlyAccounts} accounts worth ${fmtEUR(summary.newOnlyEarnings)} appear only in theirs.`}
              />
              <ReconcilingItem
                label="Restatement of shared months"
                amount={summary.overlapDelta}
                detail={`Across the ${summary.overlapMonths.length} months both cover, they now report ${fmtEUR(summary.newOverlapTotal)} against the ${fmtEUR(summary.oldOverlapTotal)} of their original reconciliation${applyMargin ? ', both net of the 5% partner margin' : ', comparing their gross figures to our net ones'}.`}
              />
              <ReconcilingItem
                label="Transaction fees they deduct"
                amount={-controls.transactionFees}
                detail={ledgerFees
                  ? `They deduct ${fmtEURExact(controls.transactionFees)} as fees Adwin bears. Their own Finance ledger shows ${fmtEURExact(ledgerFees.total)} of fees across ${ledgerFees.rows} settlements, and dates only ${fmtEUR(ledgerFees.reconPeriod)} of them to Mar 23 – Nov 24 against the ${fmtEUR(dataset.controls.transactionFeeLines['Transaction fees Mar 23 - Nov 24'] ?? 0)} they claim for that period. This is the largest single disputable line in their chain.`
                  : ''}
                severity="high"
              />
              <ReconcilingItem
                label="Their headline versus their own per-offer column"
                amount={controls.headlineVsPerOfferGap}
                detail={`Their headline earnings figure adds period 2 gross while their per-offer "Adwin earnings" column margins it. The headline is ${fmtEURExact(Math.abs(controls.headlineVsPerOfferGap))} higher, so correcting it would reduce what they owe.`}
                severity="medium"
              />
              <ReconcilingItem
                label="Accounts in their report but not in adwstats"
                amount={summary.earningsMissingFromAdwstats}
                detail={`${summary.accountsMissingFromAdwstats} Affiliation Cloud ids in their workbook have no matching account in adwstats, so we hold no independent evidence for ${fmtEUR(summary.earningsMissingFromAdwstats)} of the earnings they report.`}
                severity="medium"
              />
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Scope gaps */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'coverage' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Where the two reconciliations disagree about what exists. Accounts are counted here because
            they are the level at which money is comparable: {summary.newOnlyAccounts} accounts appear only
            in the new workbook and {summary.oldOnlyAccounts} only in the original. Underneath them,
            offers were reshuffled — {summary.matchedOffers} offer codes appear on both sides,
            {' '}{summary.newOnlyOffers} only in the new report and {summary.oldOnlyOffers} only in the old,
            which is why offer counts alone say little about missing revenue. Accounts flagged as absent
            from adwstats are ones we cannot independently verify at all.
          </Explainer>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Matched accounts" value={String(summary.matchedAccounts)} hint="Present on both sides" icon={<GitCompareArrows className="h-4 w-4" />} color="emerald" />
            <StatCard label="Only in new report" value={String(summary.newOnlyAccounts)} hint={`${fmtEUR(summary.newOnlyEarnings)} they now claim`} icon={<ArrowUpRight className="h-4 w-4" />} color="cyan" />
            <StatCard label="Only in old report" value={String(summary.oldOnlyAccounts)} hint={`${fmtEUR(summary.oldOnlyEarnings)} dropped`} icon={<ArrowDownRight className="h-4 w-4" />} color="red" />
            <StatCard label="Not in adwstats" value={String(summary.accountsMissingFromAdwstats)} hint={`${fmtEUR(summary.earningsMissingFromAdwstats)} unverifiable`} icon={<Database className="h-4 w-4" />} color="amber" />
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)]">
              <h3 className="text-sm font-semibold text-white">By partner</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Partner</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Accounts new / old</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">New earnings</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Old earnings</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Change on overlap</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">New only</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Dropped</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Not in adwstats</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {coverage.map((bucket) => (
                    <tr key={bucket.partner} className="hover:bg-[hsl(225,15%,12%)]">
                      <td className="px-3 py-2 text-zinc-200 truncate max-w-[240px]">{bucket.partner}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{bucket.newAccounts} / {bucket.oldAccounts}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-200">{fmtEUR(bucket.newEarnings)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{bucket.oldEarnings ? fmtEUR(bucket.oldEarnings) : '—'}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${bucket.delta > 1 ? 'text-emerald-400' : bucket.delta < -1 ? 'text-red-400' : 'text-zinc-600'}`}>
                        {Math.abs(bucket.delta) <= 1 ? '—' : `${bucket.delta > 0 ? '+' : ''}${fmtEUR(bucket.delta)}`}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-cyan-400/80">{bucket.unmatchedNew || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-400/80">{bucket.unmatchedOld || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-400/80">{bucket.missingFromAdwstats || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Post-recon tail */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'tail' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Their original reconciliation stopped at Nov 2024, so the {summary.tailMonths.length} months
            from Dec 2024 to May 2026 have no old baseline. The only independent check available is what
            we synced from operator backends ourselves, shown alongside. A large gap on an account means
            either we are not syncing it or their figure needs support.
          </Explainer>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <StatCard label="They report, Dec 24 →" value={fmtEUR(summary.newTailTotal)} hint={`${summary.tailMonths.length} months`} icon={<FileText className="h-4 w-4" />} color="purple" />
            <StatCard label="We synced, same months" value={fmtEUR(summary.syncedTailTotal)} hint="From operator backends" icon={<Database className="h-4 w-4" />} color="emerald" />
            <StatCard
              label="Unsupported by our data"
              value={fmtEUR(summary.newTailTotal - summary.syncedTailTotal)}
              hint="Their figure less ours"
              icon={<AlertTriangle className="h-4 w-4" />}
              color="amber"
            />
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)]">
              <h3 className="text-sm font-semibold text-white">Largest tail earnings by account</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Partner / Brands</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">They report</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">We synced</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Gap</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">In adwstats</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {[...diff.rows]
                    .filter((r) => r.newTail !== 0)
                    .sort((a, b) => b.newTail - a.newTail)
                    .slice(0, 150)
                    .map((row) => (
                      <tr key={row.key} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-2">
                          <div className="text-zinc-200 truncate max-w-[200px]">{row.partner || '—'}</div>
                          <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">
                            {row.brands.slice(0, 3).join(', ') || '—'}
                            {row.brands.length > 3 && ` +${row.brands.length - 3}`}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[10px] text-zinc-400 truncate max-w-[220px]">
                          HUB {row.hubId || '—'}
                          {row.acIds.length > 0 && <span className="text-zinc-600"> · AC {row.acIds.join(', ')}</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-200">{fmtEUR(row.newTail)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-400/90">{row.syncedTail ? fmtEUR(row.syncedTail) : '—'}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${row.newTail - row.syncedTail > 1 ? 'text-amber-400' : 'text-zinc-600'}`}>
                          {fmtEUR(row.newTail - row.syncedTail)}
                        </td>
                        <td className="px-3 py-2">
                          {row.existsInAdwstats
                            ? <span className="text-[10px] text-emerald-400/80">yes</span>
                            : <span className="text-[10px] text-red-400/80">no</span>}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Their own math */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'audit' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Checks of the workbook against itself. Every figure below was re-derived from their per-offer
            rows and their Finance ledger during import; the extraction refuses to run if a control total
            does not reconcile, so any difference shown here is a real disagreement inside their file.
          </Explainer>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)]">
                <h3 className="text-sm font-semibold text-white">Earnings</h3>
              </div>
              <div className="divide-y divide-[hsl(225,15%,14%)]">
                <AuditRow label="Gross earnings, period 1 (Mar 23 – Apr 25)" value={controls.grossPeriod1} />
                <AuditRow label="Stated Adwin share, period 1" value={controls.statedPeriod1} note="5% margin applied to partner accounts" />
                <AuditRow label="Gross earnings, period 2 (May 25 – May 26)" value={controls.grossPeriod2} />
                <AuditRow label="Stated Adwin share, period 2" value={controls.statedPeriod2} note={dataset.summary.notes[0] || 'No margin applied'} />
                <AuditRow label="Their headline total" value={controls.statedTotalEarnings} emphasis />
                <AuditRow
                  label="Sum of their per-offer Adwin earnings"
                  value={controls.sumAdwinEarnings}
                  discrepancy={-controls.headlineVsPerOfferGap}
                  note="Differs from the headline because period 2 is margined here but not there"
                />
              </div>
            </div>

            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)]">
                <h3 className="text-sm font-semibold text-white">Invoices, payments and fees</h3>
              </div>
              <div className="divide-y divide-[hsl(225,15%,14%)]">
                <AuditRow label="Total invoiced (their Finance ledger)" value={controls.totalInvoiced ?? 0} />
                <AuditRow label="Total paid (settlements)" value={controls.totalPaid ?? 0} />
                <AuditRow
                  label="Invoiced but unsettled"
                  value={(controls.totalPaid ?? 0) + (controls.totalInvoiced ?? 0)}
                  note="Negative means invoices they have not yet settled"
                />
                <AuditRow label="Transaction fees, as stated" value={controls.transactionFees} emphasis />
                {ledgerFees && (
                  <AuditRow
                    label="Transaction fees implied by their ledger"
                    value={ledgerFees.total}
                    discrepancy={ledgerFees.total - controls.transactionFees}
                    note={`${ledgerFees.rows} settlements where Amount exceeds Excluding Fee`}
                  />
                )}
                <AuditRow label="Underpayment they conclude with" value={controls.underpaymentAfterFees ?? 0} emphasis />
              </div>
            </div>
          </div>

          {ledgerFees && ledgerFees.byMonth.length > 0 && (
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[hsl(225,15%,18%)]">
                <h3 className="text-sm font-semibold text-white">When their ledger actually charged fees</h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  They attribute {fmtEUR(dataset.controls.transactionFeeLines['Transaction fees Mar 23 - Nov 24'] ?? 0)} of
                  fees to Mar 23 – Nov 24, but only {fmtEUR(ledgerFees.reconPeriod)} of the ledger's fees fall in
                  those months. {fmtEUR(ledgerFees.afterApril2025)} falls after Apr 2025 and is not claimed at all.
                </p>
              </div>
              <div className="p-4 flex flex-wrap gap-2">
                {ledgerFees.byMonth.map((fee) => (
                  <div key={fee.month} className="px-2.5 py-1.5 rounded-lg bg-[hsl(225,15%,13%)] border border-[hsl(225,15%,20%)]">
                    <div className="text-[10px] text-zinc-500">{fmtMonth(fee.month)}</div>
                    <div className="text-xs text-amber-400 tabular-nums">{fmtEUR(fee.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <h3 className="text-sm font-semibold text-white mb-2">Provenance</h3>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Provenance label="Workbook" value={dataset.source} />
              <Provenance label="Extracted" value={new Date(dataset.generatedAt).toLocaleString()} />
              <Provenance label="Offers / partners" value={`${dataset.offers.length} / ${dataset.summary.partners.length}`} />
              <Provenance label="Accounts listed" value={String(dataset.accounts.rows.length)} />
              <Provenance label="Old recon rows" value={String(oldData.oldOfferTotals.rows)} />
              <Provenance label="Old recon currencies" value={oldData.diagnostics.currenciesInUse.join(', ') || 'EUR only'} />
              <Provenance label="Corrections (+ / −)" value={`${fmtEUR(oldData.corrections.positive)} / ${fmtEUR(oldData.corrections.negative)}`} />
              <Provenance label="Unpriced months" value={oldData.diagnostics.unpricedMonths.length ? String(oldData.diagnostics.unpricedMonths.length) : 'none'} />
            </dl>
            {oldData.diagnostics.unpricedMonths.length > 0 && (
              <p className="mt-3 text-[11px] text-amber-400/80">
                No exchange rate was available for: {oldData.diagnostics.unpricedMonths.join('; ')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------
const StatCard: React.FC<{
  label: string; value: string; hint?: string; icon: React.ReactNode; color: string;
}> = ({ label, value, hint, icon, color }) => {
  const colorMap: Record<string, string> = {
    amber: 'text-amber-400 border-amber-500/20 bg-amber-500/5',
    cyan: 'text-cyan-400 border-cyan-500/20 bg-cyan-500/5',
    emerald: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5',
    red: 'text-red-400 border-red-500/20 bg-red-500/5',
    purple: 'text-purple-400 border-purple-500/20 bg-purple-500/5',
    zinc: 'text-zinc-300 border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)]',
  };
  const parts = (colorMap[color] || colorMap.zinc).split(' ');
  return (
    <div className={`rounded-xl border p-3 ${parts.slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={parts[0]}>{icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-base sm:text-lg font-bold tabular-nums ${parts[0]}`}>{value}</p>
      {hint && <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>}
    </div>
  );
};

const Explainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3">
    <p className="text-xs text-zinc-400 leading-relaxed">{children}</p>
  </div>
);

const MATCH_LABELS: Record<AccountDiffRow['matchMethod'], { label: string; cls: string }> = {
  both: { label: 'Both', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  new_only: { label: 'New only', cls: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  old_only: { label: 'Dropped', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

const MatchBadge: React.FC<{ method: AccountDiffRow['matchMethod'] }> = ({ method }) => {
  const config = MATCH_LABELS[method];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${config.cls}`}>
      {config.label}
    </span>
  );
};

const OfferBreakdown: React.FC<{ row: AccountDiffRow }> = ({ row }) => {
  if (row.offers.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-3 mb-2 text-[11px] text-zinc-500">
        <span>Offers on this account over the shared period</span>
        {row.reorganised && (
          <span className="text-blue-400/80">
            Offer codes differ between the reports, so only the account total is comparable
          </span>
        )}
      </div>
      <table className="w-full text-[11px] tabular-nums">
        <thead>
          <tr className="border-b border-[hsl(225,15%,18%)]">
            <th className="text-left py-1 pr-3 text-zinc-500 font-medium">Offer</th>
            <th className="text-left py-1 pr-3 text-zinc-500 font-medium">Brand</th>
            <th className="text-right py-1 pr-3 text-zinc-500 font-medium">Old</th>
            <th className="text-right py-1 pr-3 text-zinc-500 font-medium">New</th>
            <th className="text-right py-1 pr-3 text-zinc-500 font-medium">Delta</th>
            <th className="text-right py-1 text-zinc-500 font-medium">Tail</th>
          </tr>
        </thead>
        <tbody>
          {row.offers.map((offer) => (
            <tr key={`${offer.offer}|${offer.brand}`} className="border-b border-[hsl(225,15%,13%)] last:border-0">
              <td className="py-1 pr-3 font-mono text-zinc-300 max-w-[240px] truncate">
                {offer.offer || '—'}
                {!offer.inOld && <span className="text-cyan-400/70 font-sans"> new</span>}
                {!offer.inNew && <span className="text-red-400/70 font-sans"> dropped</span>}
              </td>
              <td className="py-1 pr-3 text-zinc-500 max-w-[140px] truncate">{offer.brand || '—'}</td>
              <td className="py-1 pr-3 text-right text-zinc-400">{offer.old ? fmtEUR(offer.old) : '—'}</td>
              <td className="py-1 pr-3 text-right text-zinc-200">{offer.new ? fmtEUR(offer.new) : '—'}</td>
              <td className={`py-1 pr-3 text-right ${
                Math.abs(offer.delta) < 1 ? 'text-zinc-600' : offer.delta > 0 ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {Math.abs(offer.delta) < 1 ? '—' : `${offer.delta > 0 ? '+' : ''}${fmtEUR(offer.delta)}`}
              </td>
              <td className="py-1 text-right text-zinc-500">{offer.newTail ? fmtEUR(offer.newTail) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AccountDetail: React.FC<{ row: AccountDiffRow }> = ({ row }) => {
  if (row.monthly.length === 0 && row.offers.length === 0) {
    return <p className="text-[11px] text-zinc-500">No offers or months with a figure on either side.</p>;
  }
  return (
    <div>
      <OfferBreakdown row={row} />
      <div className="flex flex-wrap items-center gap-3 mb-2 text-[11px] text-zinc-500">
        <span>Month by month over the shared period</span>
        {row.monthsOnlyNew > 0 && (
          <span className="text-cyan-400/80">{row.monthsOnlyNew} months only in the new report</span>
        )}
        {row.monthsOnlyOld > 0 && (
          <span className="text-red-400/80">{row.monthsOnlyOld} months dropped from the new report</span>
        )}
        {row.oldCurrency && row.oldCurrency !== 'EUR' && (
          <span>Original currency {row.oldCurrency}</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {row.monthly.map((month) => (
          <div
            key={month.month}
            className={`px-2 py-1 rounded border text-[10px] tabular-nums ${
              Math.abs(month.delta) < 1
                ? 'border-[hsl(225,15%,20%)] bg-[hsl(225,15%,11%)]'
                : month.delta > 0
                  ? 'border-emerald-500/25 bg-emerald-500/5'
                  : 'border-red-500/25 bg-red-500/5'
            }`}
          >
            <div className="text-zinc-500">{fmtMonth(month.month)}</div>
            <div className="text-zinc-400">{month.old ? fmtEUR(month.old) : '—'}</div>
            <div className="text-zinc-200">{month.new ? fmtEUR(month.new) : '—'}</div>
            {Math.abs(month.delta) >= 1 && (
              <div className={month.delta > 0 ? 'text-emerald-400' : 'text-red-400'}>
                {month.delta > 0 ? '+' : ''}{fmtEUR(month.delta)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const BridgeRow: React.FC<{ step: BridgeStep }> = ({ step }) => {
  const toneMap: Record<BridgeStep['kind'], string> = {
    figure: 'text-zinc-200',
    subtotal: 'text-white font-semibold',
    deduction: 'text-red-400',
    result: 'text-amber-400 font-bold',
    gap: 'text-zinc-400',
  };
  return (
    <div className={`px-4 py-3 ${step.kind === 'subtotal' || step.kind === 'result' ? 'bg-[hsl(225,15%,12%)]' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={`text-xs ${step.kind === 'result' || step.kind === 'subtotal' ? 'text-white font-medium' : 'text-zinc-300'}`}>
            {step.label}
          </p>
          {step.note && <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{step.note}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className={`text-sm tabular-nums ${toneMap[step.kind]}`}>
            {step.amount === null ? '—' : fmtEURExact(step.amount)}
          </p>
          {step.discrepancy !== undefined && Math.abs(step.discrepancy) > 1 && (
            <p className="text-[10px] text-amber-400 tabular-nums mt-0.5">
              {step.discrepancy > 0 ? '+' : ''}{fmtEURExact(step.discrepancy)} vs their figure
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const ReconcilingItem: React.FC<{
  label: string; amount: number; detail: string; severity?: 'high' | 'medium';
}> = ({ label, amount, detail, severity }) => (
  <div className="px-4 py-3">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-xs text-white font-medium">{label}</p>
          {severity === 'high' && (
            <span className="px-1.5 py-0.5 rounded border text-[10px] bg-red-500/10 text-red-400 border-red-500/20">
              Disputable
            </span>
          )}
          {severity === 'medium' && (
            <span className="px-1.5 py-0.5 rounded border text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/20">
              Needs clarification
            </span>
          )}
        </div>
        <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{detail}</p>
      </div>
      <p className={`text-sm tabular-nums shrink-0 ${amount >= 0 ? 'text-zinc-200' : 'text-red-400'}`}>
        {amount > 0 ? '+' : ''}{fmtEUR(amount)}
      </p>
    </div>
  </div>
);

const AuditRow: React.FC<{
  label: string; value: number; note?: string; discrepancy?: number; emphasis?: boolean;
}> = ({ label, value, note, discrepancy, emphasis }) => (
  <div className={`px-4 py-2.5 ${emphasis ? 'bg-[hsl(225,15%,12%)]' : ''}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className={`text-xs ${emphasis ? 'text-white font-medium' : 'text-zinc-300'}`}>{label}</p>
        {note && <p className="text-[11px] text-zinc-500 mt-0.5">{note}</p>}
      </div>
      <div className="text-right shrink-0">
        <p className={`text-xs tabular-nums ${emphasis ? 'text-white font-semibold' : 'text-zinc-300'}`}>
          {fmtEURExact(value)}
        </p>
        {discrepancy !== undefined && Math.abs(discrepancy) > 1 && (
          <p className="text-[10px] text-amber-400 tabular-nums mt-0.5">
            {discrepancy > 0 ? '+' : ''}{fmtEURExact(discrepancy)}
          </p>
        )}
      </div>
    </div>
  </div>
);

const Provenance: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <dt className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</dt>
    <dd className="text-xs text-zinc-300 mt-0.5 break-words">{value}</dd>
  </div>
);

export default RaketechReconDiff;
